import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ArrowRight } from "lucide-react";

/** Admin → Redirects tab. Two workflows in one place:
 *  1. Rename product slug — one-click retire-and-redirect. Pick a product,
 *     type the new slug, submit. Backend renames the product AND auto-creates
 *     the redirect from old→new.
 *  2. Manual slug redirect — raw form for any custom from/to mapping (e.g.
 *     external URL you want to forward to a live product).
 *  Both are tier-scoped now that Supply and Select share the slug namespace. */
export default function SlugRedirects() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Manual redirect form
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tierManual, setTierManual] = useState("");
  const [busy, setBusy] = useState(false);
  // Rename form
  const [renameProductId, setRenameProductId] = useState("");
  const [renameNewSlug, setRenameNewSlug] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: p }] = await Promise.all([
        api.get("/admin/slug-redirects"),
        api.get("/admin/products"),
      ]);
      setRows(Array.isArray(r) ? r : []);
      setProducts(Array.isArray(p) ? p : []);
    } catch { toast.error("Could not load redirects"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = from.trim().toLowerCase();
    const t = to.trim().toLowerCase();
    if (!f || !t) { toast.error("Both slugs required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/slug-redirects", { from_slug: f, to_slug: t, tier: tierManual || null });
      setFrom(""); setTo(""); setTierManual("");
      toast.success("Redirect created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
    setBusy(false);
  };

  const rename = async (e) => {
    e.preventDefault();
    const product = products.find((p) => p.id === renameProductId);
    const newSlug = renameNewSlug.trim().toLowerCase();
    if (!product) { toast.error("Pick a product to rename"); return; }
    if (!newSlug) { toast.error("New slug required"); return; }
    if (newSlug === product.slug) { toast.error("New slug is the same as the current slug"); return; }
    if (!window.confirm(
      `Rename "${product.name}" (${product.tier || "supply"}):\n\n  ${product.slug}  →  ${newSlug}\n\n` +
      `A redirect from "${product.slug}" will be auto-created so old links still work. Proceed?`
    )) return;
    setRenameBusy(true);
    try {
      // PUT the product with only the changed slug; server auto-creates
      // the slug_redirect and returns the updated doc.
      await api.put(`/admin/products/${product.id}`, { ...product, slug: newSlug });
      setRenameProductId(""); setRenameNewSlug("");
      toast.success(`Renamed to "${newSlug}" — redirect from "${product.slug}" active.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
    setRenameBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this redirect?")) return;
    try {
      await api.delete(`/admin/slug-redirects/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const selectedProduct = products.find((p) => p.id === renameProductId);

  return (
    <div data-testid="slug-redirects-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-charcoal mb-1">Slug redirects</h2>
        <p className="text-sm text-stone max-w-2xl">
          Redirects keep old inbound links (search results, bookmarks, emails) resolving after you rename a product. Slugs are <span className="font-mono">tier-scoped</span> — the same slug can exist on Supply <em>and</em> Select simultaneously.
        </p>
      </div>

      {/* One-click rename: pick product + new slug. Server auto-creates the redirect. */}
      <div className="mb-6 p-5 rounded-xl bg-bone border border-borderSubtle" data-testid="slug-rename-panel">
        <div className="flex items-baseline justify-between mb-3">
          <p className="overline font-bold text-charcoal">One-click rename</p>
          {selectedProduct && (
            <p className="text-[11px] text-stone font-mono" data-testid="rename-preview">
              {selectedProduct.slug} <span className="text-oxblood">→</span> {renameNewSlug || "…"}
              <span className="ml-2 uppercase tracking-widest">· {selectedProduct.tier || "supply"}</span>
            </p>
          )}
        </div>
        <form onSubmit={rename} className="grid grid-cols-[2fr,2fr,auto] gap-3 items-end" data-testid="slug-rename-form">
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-stone block mb-1">Product</label>
            <select
              value={renameProductId}
              onChange={(e) => setRenameProductId(e.target.value)}
              className="w-full rounded-md border border-borderSubtle bg-white px-3 py-2 text-sm focus:border-charcoal outline-none"
              data-testid="slug-rename-product-select"
            >
              <option value="">Choose a product…</option>
              {products
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.slug} · {p.tier || "supply"}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-stone block mb-1">New slug</label>
            <input
              type="text"
              value={renameNewSlug}
              onChange={(e) => setRenameNewSlug(e.target.value)}
              placeholder="new-slug"
              className="w-full rounded-md border border-borderSubtle bg-white px-3 py-2 font-mono text-sm focus:border-charcoal outline-none"
              data-testid="slug-rename-new-input"
            />
          </div>
          <button
            type="submit"
            disabled={renameBusy || !renameProductId || !renameNewSlug.trim()}
            className="h-[38px] px-5 rounded-md bg-oxblood text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-oxblood/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="slug-rename-submit-btn"
          >
            {renameBusy ? "Renaming…" : (<><ArrowRight size={12} strokeWidth={2}/> Rename</>)}
          </button>
        </form>
      </div>

      {/* Raw redirect form (rarely used — kept for edge cases like forwarding a retired external URL). */}
      <details className="mb-8" data-testid="slug-manual-redirect-details">
        <summary className="cursor-pointer text-[11px] uppercase tracking-[0.22em] text-stone hover:text-charcoal py-2 select-none">
          Add manual redirect (advanced)
        </summary>
        <form
          onSubmit={create}
          className="mt-3 grid grid-cols-[1fr,1fr,140px,auto] gap-3 items-end p-5 rounded-xl bg-bone border border-borderSubtle"
          data-testid="slug-redirect-form"
        >
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-stone block mb-1">From (retired)</label>
            <input
              type="text"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="old-slug"
              className="w-full rounded-md border border-borderSubtle bg-white px-3 py-2 font-mono text-sm focus:border-charcoal outline-none"
              data-testid="slug-redirect-from"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-stone block mb-1">To (live)</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="new-slug"
              className="w-full rounded-md border border-borderSubtle bg-white px-3 py-2 font-mono text-sm focus:border-charcoal outline-none"
              data-testid="slug-redirect-to"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-stone block mb-1">Tier</label>
            <select
              value={tierManual}
              onChange={(e) => setTierManual(e.target.value)}
              className="w-full rounded-md border border-borderSubtle bg-white px-3 py-2 text-sm focus:border-charcoal outline-none"
              data-testid="slug-redirect-tier"
            >
              <option value="">Any</option>
              <option value="supply">Supply</option>
              <option value="select">Select</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={busy || !from.trim() || !to.trim()}
            className="h-[38px] px-5 rounded-md bg-charcoal text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-oxblood transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="slug-redirect-create-btn"
          >
            {busy ? "Adding…" : "Add"}
          </button>
        </form>
      </details>

      {loading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-stone" data-testid="slug-redirects-empty">No redirects yet.</p>
      ) : (
        <div className="overflow-x-auto" data-testid="slug-redirects-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-borderSubtle text-[11px] uppercase tracking-[0.15em] text-stone">
                <th className="py-2 pr-4">From</th>
                <th className="py-2 pr-4">To</th>
                <th className="py-2 pr-4">Tier</th>
                <th className="py-2 pr-4">Reason</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2 pr-4 w-16 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-borderSubtle/60 hover:bg-bone" data-testid={`slug-redirect-row-${r.from_slug}`}>
                  <td className="py-3 pr-4 font-mono text-charcoal">{r.from_slug}</td>
                  <td className="py-3 pr-4 font-mono text-oxblood">→ {r.to_slug}</td>
                  <td className="py-3 pr-4 text-stone text-xs uppercase tracking-widest">{r.tier || "any"}</td>
                  <td className="py-3 pr-4 text-stone">{r.reason || "manual"}</td>
                  <td className="py-3 pr-4 text-stone text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  <td className="py-3 pr-4 text-right">
                    <button
                      onClick={() => remove(r.id)}
                      className="text-stone hover:text-oxblood transition-colors"
                      title="Delete redirect"
                      data-testid={`slug-redirect-delete-${r.from_slug}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
